import { useState, useEffect } from 'react';
import { Search, Filter, Plus, Briefcase, Clock, User, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Job, BroUser } from './types';
import JobCard from './JobCard';
import JobDetails from './JobDetails';
import JobPostingForm from './JobPosting/JobPostingForm';
import MyJobsDashboard from './MyJobs/MyJobsDashboard';
import JobBoardAccessGate from './JobBoardAccessGate';
import { useJobBoard } from './hooks/useJobBoard';

interface JobBoardProps {
  user: BroUser;
  onNavigate?: (view: string) => void;
}

type JobBoardView = 'browse' | 'job-details' | 'post-job' | 'my-jobs';

const JobBoard = ({ user }: JobBoardProps) => {
  const [currentView, setCurrentView] = useState<JobBoardView>('browse');
  const [activeTab, setActiveTab] = useState<'browse' | 'my-jobs' | 'posted'>('browse');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Use real job board hook instead of mock data
  const { 
    jobs, 
    loading, 
    error: jobsError, 
    hasMore, 
    totalCount, 
    fetchJobs, 
    refreshJobs 
  } = useJobBoard({});

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
    setCurrentView('job-details');
  };

  const handleBackToBoard = () => {
    setCurrentView('browse');
    setSelectedJob(null);
  };

  const handlePostJob = () => {
    setCurrentView('post-job');
  };

  const handleJobCreated = (newJob: any) => {
    // Refresh the job list to show the new job
    refreshJobs();
    setCurrentView('browse');
  };

  const handleMyJobs = () => {
    setCurrentView('my-jobs');
  };

  const filteredJobs = (jobs || []).filter(job => {
    if (searchQuery && !job.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !job.description.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !job.required_skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))) {
      return false;
    }
    return true;
  });

  // Job Details View
  if (currentView === 'job-details' && selectedJob) {
    return (
      <div className="p-4">
        <JobDetails
          job={selectedJob}
          onBack={handleBackToBoard}
          canApply={selectedJob.employer_id !== user.id}
          isOwner={selectedJob.employer_id === user.id}
          user={user}
        />
      </div>
    );
  }

  // Job Posting View
  if (currentView === 'post-job') {
    return (
      <JobBoardAccessGate
        user={user}
        requiredLevel="post"
        onVerificationComplete={() => setCurrentView('browse')}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBackToBoard}
              className="text-tan hover:text-tan-dark"
            >
              ← Back to Jobs
            </button>
            <h2 className="text-xl font-bold text-tan">Post New Job</h2>
          </div>
          <JobPostingForm 
            user={user} 
            onBack={handleBackToBoard}
            onJobCreated={handleJobCreated}
          />
        </div>
      </JobBoardAccessGate>
    );
  }

  // My Jobs Dashboard View
  if (currentView === 'my-jobs') {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBackToBoard}
            className="text-tan hover:text-tan-dark"
          >
            ← Back to Jobs
          </button>
          <h2 className="text-xl font-bold text-tan">My Jobs</h2>
        </div>
        <MyJobsDashboard user={user} onBack={handleBackToBoard} />
      </div>
    );
  }

  // Main Job Board View
  return (
    <JobBoardAccessGate
      user={user}
      requiredLevel="browse"
      onVerificationComplete={() => window.location.reload()}
    >
      <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-tan">Job Board</h1>
          <p className="text-tan-dark text-sm">Find opportunities in the BroMentum ecosystem</p>
        </div>
        <Button
          onClick={handlePostJob}
          className="bg-tan text-navy hover:bg-tan-dark"
        >
          <Plus size={16} className="mr-1" />
          Post Job
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-navy-light rounded-lg p-1 border border-tan-dark">
        <button
          onClick={() => setActiveTab('browse')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'browse'
              ? 'bg-tan text-navy'
              : 'text-tan-dark hover:text-tan'
          }`}
        >
          <Briefcase size={16} className="inline mr-1" />
          Browse Jobs
        </button>
        <button
          onClick={() => { setActiveTab('my-jobs'); handleMyJobs(); }}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'my-jobs'
              ? 'bg-tan text-navy'
              : 'text-tan-dark hover:text-tan'
          }`}
        >
          <Clock size={16} className="inline mr-1" />
          My Work
        </button>
        <button
          onClick={() => setActiveTab('posted')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'posted'
              ? 'bg-tan text-navy'
              : 'text-tan-dark hover:text-tan'
          }`}
        >
          <User size={16} className="inline mr-1" />
          Posted
        </button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
            <Input
              placeholder="Search jobs, skills, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-navy-light border-tan-dark text-tan placeholder-tan-dark"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
          >
            <Filter size={16} />
          </Button>
        </div>

        {showFilters && (
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-tan text-sm font-medium mb-1 block">Budget Range</label>
                <select className="w-full bg-navy border border-tan-dark rounded text-tan p-2 text-sm">
                  <option value="">Any Budget</option>
                  <option value="0-100">0-100 BroScore</option>
                  <option value="100-500">100-500 BroScore</option>
                  <option value="500-1000">500-1000 BroScore</option>
                  <option value="1000+">1000+ BroScore</option>
                </select>
              </div>
              <div>
                <label className="text-tan text-sm font-medium mb-1 block">Experience Level</label>
                <select className="w-full bg-navy border border-tan-dark rounded text-tan p-2 text-sm">
                  <option value="">Any Level</option>
                  <option value="entry">Entry Level</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" className="bg-tan text-navy hover:bg-tan-dark">
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Job Statistics */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-navy-light rounded-lg p-3 border border-tan-dark text-center">
          <div className="text-tan font-bold text-lg">{totalCount}</div>
          <div className="text-tan-dark text-xs">Total Jobs</div>
        </div>
        <div className="bg-navy-light rounded-lg p-3 border border-tan-dark text-center">
          <div className="text-tan font-bold text-lg">
            {filteredJobs.length > 0 ? Math.round(filteredJobs.reduce((sum, job) => sum + (job.budget_max || 0), 0) / filteredJobs.length) : 0}
          </div>
          <div className="text-tan-dark text-xs">Avg Budget</div>
        </div>
        <div className="bg-navy-light rounded-lg p-3 border border-tan-dark text-center">
          <div className="text-tan font-bold text-lg">
            {filteredJobs.reduce((sum, job) => sum + (job.applications_count || 0), 0)}
          </div>
          <div className="text-tan-dark text-xs">Applications</div>
        </div>
      </div>
      
      {/* Error Display */}
      {jobsError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="text-red-400 text-sm font-medium">Error loading jobs</div>
          <div className="text-red-300 text-xs">{jobsError}</div>
          <Button 
            onClick={() => refreshJobs()} 
            size="sm" 
            className="mt-2 bg-red-600 hover:bg-red-700 text-white"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Featured Jobs Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-4 border border-tan-dark">
        <div className="flex items-center gap-2 mb-2">
          <Star className="text-yellow-400" size={16} />
          <span className="text-white font-bold text-sm">Featured Jobs</span>
        </div>
        <p className="text-purple-100 text-xs">
          High-priority projects from verified employers with premium budgets
        </p>
      </div>

      {/* Job Listings */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-tan-dark">Loading jobs...</div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="mx-auto text-tan-dark mb-2" size={48} />
            <div className="text-tan-dark">No jobs found</div>
            <div className="text-tan-dark text-sm">Try adjusting your search or filters</div>
          </div>
        ) : (
          filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onJobClick={handleJobClick}
              showApplicationButton={job.employer_id !== user.id}
            />
          ))
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center pt-4">
          <Button
            onClick={() => fetchJobs()}
            variant="outline"
            disabled={loading}
            className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
          >
            {loading ? 'Loading...' : 'Load More Jobs'}
          </Button>
        </div>
      )}
      </div>
    </JobBoardAccessGate>
  );
};

export default JobBoard;